import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  FiCheckCircle,
  FiPackage,
  FiPercent,
  FiRefreshCw,
  FiTrash2,
  FiUsers,
  FiChevronDown,
  FiPlus,
  FiActivity,
  FiShield,
  FiStar,
  FiGrid,
  FiEdit,
  FiEye,
  FiLayers,
  FiCpu,
  FiUploadCloud
} from "react-icons/fi";
import api from "../services/api";
import { useNotification } from "../context/NotificationContext";
import { useTranslation } from "react-i18next";
import VerifiedSellerBadge from "../components/VerifiedSellerBadge";
import { localizedCategoryName } from "../utils/i18nContent";

const AdminPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [categories, setCategories] = useState([]);
  const [verificationRequests, setVerificationRequests] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [newCouponCode, setNewCouponCode] = useState("");
  const [newDiscount, setNewDiscount] = useState("");
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryParentId, setNewCategoryParentId] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmLabel, setConfirmLabel] = useState("");
  const [pushTitle, setPushTitle] = useState("");
  const [pushMessage, setPushMessage] = useState("");
  const [productRequests, setProductRequests] = useState([]);
  const [reports, setReports] = useState([]);
  const [productReview, setProductReview] = useState({});
  const [verificationReview, setVerificationReview] = useState({});
  const [featuredImageByProduct, setFeaturedImageByProduct] = useState({});
  const [translationJob, setTranslationJob] = useState(null);
  const [translationStarting, setTranslationStarting] = useState(false);
  const [showFeaturedImageModal, setShowFeaturedImageModal] = useState(false);
  const [targetProductForFeatured, setTargetProductForFeatured] = useState(null);
  const [tempFeaturedImage, setTempFeaturedImage] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, productsRes, couponsRes, categoriesRes, verificationRes, requestsRes, reportsRes] = await Promise.all([
        api.get("/admin/users/"),
        api.get("/admin/products/"),
        api.get("/admin/coupons/"),
        api.get("/categories/"),
        api.get("/admin/verification-requests/"),
        api.get("/admin/products/requests/"),
        api.get("/admin/reports/"),
      ]);
      setUsers(usersRes.data);
      setProducts(productsRes.data);
      setCoupons(couponsRes.data);
      setCategories(categoriesRes.data.results || categoriesRes.data);
      setVerificationRequests(verificationRes.data);
      setProductRequests(requestsRes.data);
      setReports(reportsRes.data);
    } catch (error) {
      console.error("Failed to fetch admin data", error);
      showNotification("Failed to load admin data", "danger");
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchTranslationJob = useCallback(async () => {
    try {
      const response = await api.get("/admin/translations/backfill/");
      setTranslationJob(response.data.job || null);
    } catch (error) {
      console.error("Failed to fetch translation job", error);
    }
  }, []);

  useEffect(() => {
    if (activeTab !== "translations" && !translationJob?.running) {
      return undefined;
    }
    fetchTranslationJob();
    const interval = window.setInterval(fetchTranslationJob, 1500);
    return () => window.clearInterval(interval);
  }, [activeTab, translationJob?.running, fetchTranslationJob]);

  const metrics = useMemo(
    () => [
      {
        label: "Total Users",
        value: users.length,
        icon: <FiUsers size={22} />,
        bgColor: "bg-blue-500/10 text-blue-500",
      },
      {
        label: "Total Products",
        value: products.length,
        icon: <FiPackage size={22} />,
        bgColor: "bg-emerald-500/10 text-emerald-500",
      },
      {
        label: "Verified Sellers",
        value: users.filter((user) => user.supplier_profile?.is_verified).length,
        icon: <FiCheckCircle size={22} />,
        bgColor: "bg-orange-500/10 text-chafadi-primary",
      },
      {
        label: "Active Coupons",
        value: coupons.length,
        icon: <FiPercent size={22} />,
        bgColor: "bg-rose-500/10 text-rose-500",
      },
    ],
    [coupons.length, products.length, users]
  );

  const handleToggleStaff = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/toggle-staff/`);
      showNotification("Authority updated", "success");
      fetchData();
    } catch (error) {
      showNotification("Failed to update admin role", "danger");
    }
  };

  const handleSetStaffByEmail = async (isStaff) => {
    if (!adminEmail.trim()) {
      showNotification("Identity signal required", "warning");
      return;
    }
    try {
      await api.post("/admin/users/set-staff/", {
        email: adminEmail.trim(),
        is_staff: isStaff,
      });
      showNotification(isStaff ? "Authority granted" : "Authority revoked", "success");
      setAdminEmail("");
      fetchData();
    } catch (error) {
      showNotification("Sequence error during role update", "danger");
    }
  };

  const handleToggleVerification = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/toggle-verification/`);
      showNotification("Verification state synchronized", "success");
      fetchData();
    } catch (error) {
      showNotification("Failed to update verification", "danger");
    }
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    if (!newCouponCode || !newDiscount) return;
    try {
      const formData = new FormData();
      formData.append("code", newCouponCode);
      formData.append("discount_percentage", newDiscount);
      await api.post("/admin/coupons/", formData);
      showNotification("Promo node initialized", "success");
      setNewCouponCode("");
      setNewDiscount("");
      fetchData();
    } catch (error) {
      showNotification("Failed to initialize promo node", "danger");
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;
    try {
      const formData = new FormData();
      formData.append("name", newCategoryName.trim());
      if (newCategoryParentId) {
        formData.append("parent_id", newCategoryParentId);
      }
      await api.post("/admin/categories/", formData);
      showNotification("Market classification created", "success");
      setNewCategoryName("");
      setNewCategoryParentId("");
      fetchData();
    } catch (error) {
      showNotification("Failed to create classification", "danger");
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    try {
      await api.delete(`/admin/categories/?id=${categoryId}`);
      showNotification("Category classification purged", "warning");
      fetchData();
    } catch (error) {
      showNotification("Failed to delete category", "danger");
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/admin/users/${userId}/`);
      showNotification("Entity purged", "warning");
      fetchData();
    } catch (error) {
      showNotification("Purge protocol failed", "danger");
    }
  };

  const handleDeleteProduct = async (productId) => {
    try {
      await api.delete(`/admin/products/${productId}/`);
      showNotification("Asset de-initialized", "warning");
      fetchData();
    } catch (error) {
      showNotification("De-initialization failed", "danger");
    }
  };
  const handleToggleFeatured = async (product) => {
    const productId = product.id;
    const isFeatured = product.is_featured;
    const selectedFeaturedImage = featuredImageByProduct[productId];
    try {
      const formData = new FormData();
      formData.append("is_featured", String(!isFeatured));
      formData.append("featured_rank", "0");
      if (selectedFeaturedImage) {
        formData.append("featured_image", selectedFeaturedImage);
      }
      await api.post(`/admin/products/${productId}/set-featured/`, formData);
      showNotification("Featured state synchronized", "success");
      setFeaturedImageByProduct((prev) => {
        const next = { ...prev };
        delete next[productId];
        return next;
      });
      fetchData();
    } catch (error) {
      showNotification(
        error.response?.data?.error || "Failed to update featured state",
        "danger"
      );
    }
  };

  const handleFeatureButtonClick = (product) => {
    // If it's already featured, unfeature it directly without asking for an image
    if (product.is_featured) {
      handleToggleFeatured(product);
      return;
    }
    // If not featured, check if it has a featured image or a pending file upload
    const hasFeaturedImage = (product.featured_image_url && product.featured_image_url.trim() !== "") || featuredImageByProduct[product.id];
    if (hasFeaturedImage) {
      handleToggleFeatured(product);
    } else {
      // Prompt the admin clearly to upload a landscape featured image
      setTargetProductForFeatured(product);
      setTempFeaturedImage(null);
      setShowFeaturedImageModal(true);
    }
  };

  const handleUploadAndFeature = async () => {
    if (!targetProductForFeatured) return;
    if (!tempFeaturedImage) {
      showNotification("Please select a landscape image first.", "warning");
      return;
    }
    const productId = targetProductForFeatured.id;
    try {
      const formData = new FormData();
      formData.append("is_featured", "true");
      formData.append("featured_rank", "0");
      formData.append("featured_image", tempFeaturedImage);
      
      await api.post(`/admin/products/${productId}/set-featured/`, formData);
      showNotification("Product featured successfully", "success");
      
      setFeaturedImageByProduct((prev) => {
        const next = { ...prev };
        delete next[productId];
        return next;
      });
      setShowFeaturedImageModal(false);
      setTargetProductForFeatured(null);
      setTempFeaturedImage(null);
      fetchData();
    } catch (error) {
      showNotification(
        error.response?.data?.error || "Failed to update featured state",
        "danger"
      );
    }
  };

  const handleBroadcastNotification = async (e) => {
    e.preventDefault();
    if (!pushTitle.trim() && !pushMessage.trim()) {
      showNotification("Notification title or description is required", "warning");
      return;
    }
    try {
      await api.post("/admin/notifications/broadcast/", {
        title: pushTitle.trim(),
        message: pushMessage.trim(),
      });
      showNotification("Broadcast notification sent", "success");
      setPushTitle("");
      setPushMessage("");
    } catch (error) {
      showNotification("Failed to send broadcast notification", "danger");
    }
  };

  const handleStartTranslationJob = async () => {
    setTranslationStarting(true);
    try {
      const response = await api.post("/admin/translations/backfill/", {});
      setTranslationJob(response.data.job || null);
      showNotification("Translation job started", "success");
    } catch (error) {
      const message =
        error.response?.data?.error || "Failed to start translation job";
      if (error.response?.status === 409 && error.response?.data?.job) {
        setTranslationJob(error.response.data.job);
      }
      showNotification(message, "warning");
    } finally {
      setTranslationStarting(false);
    }
  };

  const handleVerificationReview = async (userId) => {
    const current = verificationReview[userId] || {};
    if (!current.status) {
      showNotification("Select Verify or Reject first", "warning");
      return;
    }
    if (current.status === "rejected" && !current.reason?.trim()) {
      showNotification("Reject reason is required", "warning");
      return;
    }
    try {
      await api.post(`/admin/verification-requests/${userId}/review/`, {
        status: current.status,
        reason: current.reason || "",
      });
      showNotification("Verification request updated", "success");
      fetchData();
    } catch (error) {
      showNotification(
        error.response?.data?.error || "Failed to review verification request",
        "danger"
      );
    }
  };

  const handleProductReview = async (productId) => {
    const current = productReview[productId] || {};
    if (!current.status) {
      showNotification("Select Accept or Reject first", "warning");
      return;
    }
    if (current.status === "rejected" && !current.reason?.trim()) {
      showNotification("Reject reason is required", "warning");
      return;
    }
    try {
      await api.post(`/admin/products/${productId}/review/`, {
        status: current.status,
        rejection_reason: current.reason || "",
      });
      showNotification(`Product ${current.status}`, "success");
      fetchData();
    } catch (error) {
      showNotification(
        error.response?.data?.error || "Failed to review product",
        "danger"
      );
    }
  };

  const handleAICheck = async (productId) => {
    try {
      showNotification("AI is analyzing product details...", "info");
      await api.post(`/admin/products/${productId}/ai-check/`);
      showNotification("AI Analysis complete", "success");
      fetchData();
    } catch (error) {
      showNotification("AI verification failed", "danger");
    }
  };

  const handleModeration = async (reportId, action) => {
    try {
      await api.post(`/admin/reports/${reportId}/resolve/`, { action });
      showNotification(`Report processed: ${action}`, "success");
      fetchData();
    } catch (error) {
      showNotification("Failed to process report", "danger");
    }
  };


  const openConfirm = (action, label) => {
    setConfirmAction(() => action);
    setConfirmLabel(label);
    setShowConfirm(true);
  };

  const executeConfirm = async () => {
    if (confirmAction) {
      await confirmAction();
    }
    setShowConfirm(false);
    setConfirmAction(null);
    setConfirmLabel("");
  };

  const parentCategories = categories.filter((category) => !category.parent_id);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] gap-8 animate-fade-in bg-theme-bg">
        <div className="relative">
          <div className="w-24 h-24 border-4 border-chafadi-primary/20 border-t-chafadi-primary rounded-full animate-spin" />
          <FiActivity className="absolute inset-0 m-auto text-chafadi-primary animate-pulse" size={24} />
        </div>
        <p className="text-[10px] font-black text-theme-muted uppercase tracking-[0.5em]">Establishing Admin Link...</p>
      </div>
    );
  }

  return (
    <div className="bg-[#fcfcfc] min-h-screen font-sans text-[#1d1d1f]">
      <div className="max-w-7xl mx-auto px-4 py-8">
        
        {/* ADMIN HEADER: Minimal and Stylish */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12 border-b border-gray-100 pb-8">
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 rounded-2xl bg-white border border-gray-200 flex items-center justify-center text-[#ff7a00] shadow-sm">
              <FiShield size={40} />
            </div>
            <div className="space-y-1">
              <h1 className="text-2xl font-bold flex items-center gap-3">
                Admin Console
              </h1>
              <p className="text-sm text-gray-400 font-medium lowercase first-letter:uppercase tracking-tight">
                {t?.("admin.manageMarketplace") || "Manage marketplace activity and user accounts"}
              </p>
              <div className="flex gap-4 mt-2">
                <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                  <FiUsers className="text-[#ff7a00]" /> {users.length} Users
                </div>
                <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                  <FiPackage className="text-[#ff7a00]" /> {products.length} Products
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button 
              onClick={fetchData} 
              className="h-11 px-6 bg-white border border-gray-200 text-gray-600 text-xs font-bold rounded-lg hover:border-gray-300 transition-all flex items-center gap-2"
            >
              <FiRefreshCw className={loading ? "animate-spin" : ""} size={16} /> 
              {loading ? "Synchronizing..." : "Refresh Data"}
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-12">
          
          {/* SIDEBAR NAVIGATION: Profile Style */}
          <aside className="lg:w-64 shrink-0">
            <nav className="flex flex-col gap-1">
              {[
                { id: "overview", label: "Overview", icon: FiActivity },
                { id: "users", label: "Users", icon: FiUsers },
                { id: "reports", label: "Reports", icon: FiShield },
                { id: "requests", label: "Product Requests", icon: FiShield },
                { id: "products", label: "Products", icon: FiPackage },
                { id: "categories", label: "Categories", icon: FiGrid },
                { id: "verification", label: "Verification", icon: FiShield },
                { id: "coupons", label: "Coupons", icon: FiPercent },
                { id: "translations", label: "Translation Job", icon: FiRefreshCw },
                { id: "push", label: "Push Notification", icon: FiCheckCircle },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`flex items-center gap-4 px-5 py-4 rounded-xl text-sm font-bold transition-all ${
                    activeTab === item.id
                      ? "bg-[#ff7a00] text-white shadow-lg shadow-orange-500/10"
                      : "text-gray-500 hover:bg-gray-50 hover:text-[#ff7a00]"
                  }`}
                >
                  <item.icon size={18} />
                  {item.label}
                </button>
              ))}
            </nav>
            
            <div className="mt-12 pt-8 border-t border-gray-100 space-y-6">
              <div className="space-y-4 text-center p-6 bg-gray-50 rounded-2xl">
                 <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em]">Security Protocol</h4>
                 <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto border border-gray-100 text-emerald-500">
                    <FiShield size={20} />
                 </div>
                 <p className="text-[9px] font-bold text-gray-400 uppercase leading-relaxed">Admin access authorized for this node</p>
              </div>
            </div>
          </aside>

          {/* CONTENT AREA */}
          <main className="flex-1 min-w-0">
            <div className="animate-fade-in">
              {activeTab === "overview" && (
                <div className="space-y-12">
                  {/* METRICS GRID */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {metrics.map((metric) => (
                      <div key={metric.label} className="bg-white border border-gray-100 rounded-3xl p-8 flex items-center justify-between group hover:shadow-md transition-all">
                        <div className="space-y-1">
                           <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{metric.label}</p>
                           <h3 className="text-3xl font-black text-gray-800">{metric.value}</h3>
                        </div>
                        <div className={`w-14 h-14 rounded-2xl ${metric.bgColor} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                          {metric.icon}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* RECENT PRODUCTS */}
                    <section className="bg-white border border-gray-100 rounded-3xl p-8">
                      <div className="flex items-center justify-between mb-8">
                        <h2 className="text-lg font-bold">Recent Listings</h2>
                        <button onClick={() => setActiveTab("products")} className="text-[10px] font-bold text-[#ff7a00] uppercase tracking-widest hover:underline">View All</button>
                      </div>
                      <div className="space-y-4">
                        {products.slice(0, 5).map((product) => (
                          <div key={product.id} className="flex gap-4 items-center py-3 border-b border-gray-50 last:border-0 grow">
                             <div className="w-12 h-12 rounded-xl bg-gray-50 overflow-hidden border border-gray-100 shrink-0">
                                <img src={product.thumbnail_url || "https://placehold.co/128"} alt="" className="w-full h-full object-cover" />
                             </div>
                             <div className="flex-1 min-w-0">
                                <h4 className="text-xs font-bold truncate">{product.name}</h4>
                                <p className="text-[9px] text-gray-400 font-bold uppercase tracking-wider mt-0.5">{localizedCategoryName(product.category, i18n.language) || "Uncategorized"}</p>
                             </div>
                             <div className="text-xs font-bold text-[#ff7a00]">{product.price || "---"}</div>
                          </div>
                        ))}
                      </div>
                    </section>

                    <div className="space-y-8">
                      {/* CATEGORY BOX */}
                      <section className="bg-gray-900 rounded-3xl p-8 text-white relative overflow-hidden group">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-[#ff7a00]/10 rounded-full -mr-16 -mt-16 blur-xl group-hover:bg-[#ff7a00]/20 transition-all duration-1000" />
                        <h2 className="text-lg font-bold mb-6 relative z-10">New Classification</h2>
                        <form onSubmit={handleCreateCategory} className="space-y-4 relative z-10">
                          <input
                            type="text"
                            className="w-full h-12 bg-white/5 border border-white/10 rounded-xl px-4 font-bold text-xs uppercase tracking-widest text-white outline-none transition-all placeholder:text-white/20 focus:bg-white/10"
                            placeholder="CATEGORY NAME..."
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            required
                          />
                          <div className="relative">
                            <select
                              className="w-full h-12 bg-white/5 border border-white/10 rounded-xl px-4 font-bold text-[10px] uppercase tracking-widest text-white outline-none appearance-none cursor-pointer focus:bg-white/10"
                              value={newCategoryParentId}
                              onChange={(e) => setNewCategoryParentId(e.target.value)}
                            >
                              <option value="" className="bg-gray-900">MASTER NODE</option>
                              {parentCategories.map((category) => (
                                <option key={category.id} value={category.id} className="bg-gray-900">{localizedCategoryName(category, i18n.language).toUpperCase()}</option>
                              ))}
                            </select>
                            <FiChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none" />
                          </div>
                          <button type="submit" className="w-full h-12 bg-[#ff7a00] text-white font-bold rounded-xl text-[10px] uppercase tracking-widest hover:bg-[#e66c00] transition-all flex items-center justify-center gap-2">
                            <FiPlus size={16} /> Create Node
                          </button>
                        </form>
                      </section>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "reports" && (
                <div className="bg-white border border-gray-100 rounded-3xl p-8 overflow-hidden">
                  <div className="flex items-center justify-between mb-10">
                    <h2 className="text-xl font-bold">Objectionable Content Reports</h2>
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{reports.length} Reports</span>
                  </div>
                  <div className="overflow-x-auto -mx-8">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b border-gray-50">
                           <th className="px-8 pb-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Reporter</th>
                           <th className="px-8 pb-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Target</th>
                           <th className="px-8 pb-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Reason</th>
                           <th className="px-8 pb-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest text-right">Moderation</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {reports.filter(r => !r.is_resolved).map((report) => (
                          <tr key={report.id} className="group hover:bg-gray-50 transition-all">
                            <td className="px-8 py-6">
                              <div className="font-bold text-sm text-gray-800">{report.reporter?.first_name} {report.reporter?.last_name}</div>
                              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mt-1">{report.reporter?.email}</div>
                            </td>
                            <td className="px-8 py-6">
                              {report.content_type === 'product' && report.reported_product ? (
                                <Link to={`/products/${report.content_id}`} className="flex items-center gap-3 group/target">
                                  <div className="h-10 w-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 border border-gray-200">
                                    <img 
                                      src={report.reported_product.thumbnail_url} 
                                      alt="" 
                                      className="h-full w-full object-cover group-hover/target:scale-110 transition-transform"
                                    />
                                  </div>
                                  <div>
                                    <div className="font-bold text-sm text-gray-800 group-hover/target:text-[#ff7a00] transition-colors">{report.reported_product.name}</div>
                                    <div className="text-[10px] font-bold text-[#ff7a00] uppercase tracking-wider mt-0.5">Product #{report.content_id}</div>
                                  </div>
                                </Link>
                              ) : (
                                <>
                                  <div className="font-bold text-sm text-gray-800">{report.reported_user?.email || 'User content'}</div>
                                  <div className="text-[10px] font-bold text-[#ff7a00] uppercase tracking-wider mt-1">{report.content_type} #{report.content_id}</div>
                                </>
                              )}
                            </td>
                            <td className="px-8 py-6 text-sm italic text-gray-500">"{report.reason}"</td>
                            <td className="px-8 py-6 text-right">
                              <div className="flex gap-2 justify-end">
                                <button
                                  onClick={() => handleModeration(report.id, 'ignore')}
                                  className="h-8 px-3 bg-gray-50 text-gray-500 rounded-lg hover:bg-gray-100 transition-all text-[9px] font-bold uppercase tracking-wider border border-gray-100"
                                >
                                  Ignore
                                </button>
                                <button
                                  onClick={() => handleModeration(report.id, 'remove')}
                                  className="h-8 px-3 bg-orange-50 text-[#ff7a00] rounded-lg hover:bg-orange-500 hover:text-white transition-all text-[9px] font-bold uppercase tracking-wider border border-orange-100"
                                >
                                  Remove Product
                                </button>
                                <button
                                  onClick={() => handleModeration(report.id, 'eject_user')}
                                  className="h-8 px-3 bg-red-50 text-red-500 rounded-lg hover:bg-red-500 hover:text-white transition-all text-[9px] font-bold uppercase tracking-wider border border-red-100"
                                >
                                  Ban User
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {reports.length === 0 && (
                      <div className="p-12 text-center text-sm font-bold text-gray-400 uppercase tracking-widest">Safe Environment: Zero Reports Detected</div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === "users" && (
                <div className="bg-white border border-gray-100 rounded-3xl p-8 overflow-hidden">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                    <h2 className="text-xl font-bold">Entity Directory</h2>
                    <div className="flex gap-2">
                       <input
                          type="email"
                          className="h-11 bg-gray-50 border border-gray-100 rounded-lg px-4 font-bold text-xs uppercase tracking-wider text-[#1d1d1f] outline-none transition-all placeholder:text-gray-300 w-full md:w-64"
                          placeholder="USER EMAIL..."
                          value={adminEmail}
                          onChange={(e) => setAdminEmail(e.target.value)}
                        />
                        <button onClick={() => handleSetStaffByEmail(true)} className="h-11 px-4 bg-gray-900 text-white font-bold text-[10px] rounded-lg uppercase tracking-wider hover:bg-black transition-all">Grant</button>
                    </div>
                  </div>
                  
                  <div className="overflow-x-auto -mx-8">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b border-gray-50">
                           <th className="px-8 pb-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">User Details</th>
                           <th className="px-8 pb-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Type</th>
                           <th className="px-8 pb-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Verification</th>
                           <th className="px-8 pb-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Role</th>
                           <th className="px-8 pb-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {users.map((user) => (
                          <tr key={user.id} className="group hover:bg-gray-50 transition-all">
                            <td className="px-8 py-6">
                              <div className="font-bold text-sm text-gray-800 flex items-center gap-2">
                                {user.first_name} {user.last_name}
                                <VerifiedSellerBadge isVerified={user.supplier_profile?.is_verified} size={16} />
                              </div>
                              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mt-1">{user.email}</div>
                            </td>
                            <td className="px-8 py-6">
                              <span className="px-3 py-1 bg-gray-50 border border-gray-100 text-[9px] font-bold rounded-lg uppercase tracking-wider text-gray-500">{user.user_type}</span>
                            </td>
                            <td className="px-8 py-6">
                              <button
                                onClick={() => handleToggleVerification(user.id)}
                                className={`px-4 py-1.5 rounded-lg text-[9px] font-bold uppercase tracking-wider transition-all border flex items-center gap-2 ${
                                  user.supplier_profile?.is_verified 
                                    ? "bg-emerald-50 text-emerald-600 border-emerald-100" 
                                    : "bg-white text-[#ff7a00] border-orange-100 hover:bg-[#ff7a00] hover:text-white"
                                }`}
                              >
                                {user.supplier_profile?.is_verified ? <><FiCheckCircle /> Verified</> : "Verify"}
                              </button>
                            </td>
                            <td className="px-8 py-6">
                              <button
                                onClick={() => handleToggleStaff(user.id)}
                                className={`px-4 py-1.5 rounded-lg text-[9px] font-bold uppercase tracking-wider transition-all border flex items-center gap-2 ${
                                  user.is_staff 
                                    ? "bg-gray-800 text-white border-gray-800" 
                                    : "bg-white border-gray-200 text-gray-400 hover:border-gray-800 hover:text-gray-800"
                                }`}
                              >
                                {user.is_staff ? <><FiShield /> Admin</> : "Upgrade"}
                              </button>
                            </td>
                            <td className="px-8 py-6 text-right">
                              <button
                                onClick={() => openConfirm(() => handleDeleteUser(user.id), `PURGE ENTITY ${user.email.toUpperCase()}`)}
                                className="w-9 h-9 rounded-lg bg-red-50 text-red-400 hover:bg-red-500 hover:text-white transition-all flex items-center justify-center ml-auto border border-red-100"
                              >
                                <FiTrash2 size={14} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === "products" && (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                  {products.map((product) => (
                    <div key={product.id} className="bg-white border border-gray-100 rounded-3xl p-6 hover:shadow-xl hover:border-orange-500/10 transition-all duration-350 group flex flex-col justify-between">
                      <div>
                        {/* CARD TOP BADGES */}
                        <div className="flex flex-wrap gap-2 mb-4 justify-between items-center">
                          <span className={`px-2.5 py-1 rounded-full text-[9px] font-extrabold uppercase tracking-widest ${
                            product.status === 'active' 
                              ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' 
                              : 'bg-rose-50 text-rose-600 border border-rose-100'
                          }`}>
                            {product.status || 'draft'}
                          </span>
                          
                          <div className="flex gap-1">
                            {product.is_featured && (
                              <span className="px-2.5 py-1 bg-amber-50 text-amber-600 border border-amber-100 rounded-full text-[9px] font-extrabold uppercase tracking-widest flex items-center gap-1">
                                <FiStar className="fill-current text-amber-500" size={10} /> Featured
                              </span>
                            )}
                            {product.ai_checked && (
                              <span className="px-2.5 py-1 bg-indigo-50 text-indigo-600 border border-indigo-100 rounded-full text-[9px] font-extrabold uppercase tracking-widest flex items-center gap-1">
                                <FiCpu size={10} /> AI Checked
                              </span>
                            )}
                          </div>
                        </div>

                        {/* PRODUCT IMAGE SECTION */}
                        <div className="relative aspect-[4/3] rounded-2xl bg-gray-50 overflow-hidden border border-gray-100 shrink-0 mb-5 group-hover:shadow-md transition-shadow">
                          <img 
                            src={product.thumbnail_url || "https://placehold.co/400x300"} 
                            alt={product.name} 
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" 
                          />
                          <div className="absolute bottom-3 left-3 bg-black/75 text-white font-black text-xs px-3 py-1.5 rounded-xl backdrop-blur-sm shadow-sm">
                            {product.price || "Negotiable"}
                          </div>
                        </div>

                        {/* DETAILS SECTION */}
                        <div className="space-y-3">
                          <div>
                            <h4 className="font-bold text-gray-800 text-base leading-snug truncate group-hover:text-[#ff7a00] transition-colors">
                              {product.name}
                            </h4>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1 flex items-center gap-1.5">
                              <FiUsers className="text-[#ff7a00]" /> 
                              <span>By {product.supplier?.first_name || "Unknown Seller"}</span>
                              {product.supplier?.supplier_profile?.is_verified && (
                                <VerifiedSellerBadge isVerified={true} size={12} />
                              )}
                            </p>
                          </div>

                          {/* BILINGUAL STRINGS DISPLAY */}
                          {(product.name_en !== product.name || product.name_fr !== product.name) && (
                            <div className="bg-gray-50/50 border border-gray-100 rounded-xl p-2.5 text-[9px] font-bold text-gray-400 space-y-1">
                              <div className="flex justify-between">
                                <span className="uppercase text-gray-300">EN Name:</span>
                                <span className="text-gray-500 font-semibold truncate max-w-[150px]">{product.name_en || product.name}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="uppercase text-gray-300">FR Name:</span>
                                <span className="text-gray-500 font-semibold truncate max-w-[150px]">{product.name_fr || product.name}</span>
                              </div>
                            </div>
                          )}

                          {/* DESCRIPTION */}
                          {product.description && (
                            <p className="text-xs text-gray-500 leading-relaxed font-medium line-clamp-2">
                              {product.description}
                            </p>
                          )}

                          {/* TECH SPECS GRID (2x2) */}
                          <div className="grid grid-cols-2 gap-3 pt-2">
                            <div className="flex items-center gap-2 bg-gray-50 border border-gray-100/50 rounded-xl p-2.5">
                              <FiPackage size={14} className="text-gray-400" />
                              <div>
                                <p className="text-[8px] font-black text-gray-400 uppercase tracking-wider">In Stock</p>
                                <p className="text-[11px] font-extrabold text-gray-700">{product.quantity_in_stock ?? 0} units</p>
                              </div>
                            </div>

                            <div className="flex items-center gap-2 bg-gray-50 border border-gray-100/50 rounded-xl p-2.5">
                              <FiLayers size={14} className="text-gray-400" />
                              <div>
                                <p className="text-[8px] font-black text-gray-400 uppercase tracking-wider">Min Order</p>
                                <p className="text-[11px] font-extrabold text-gray-700">{product.minimum_order_quantity ?? 1} units</p>
                              </div>
                            </div>

                            <div className="flex items-center gap-2 bg-gray-50 border border-gray-100/50 rounded-xl p-2.5">
                              <FiEye size={14} className="text-gray-400" />
                              <div>
                                <p className="text-[8px] font-black text-gray-400 uppercase tracking-wider">Views Count</p>
                                <p className="text-[11px] font-extrabold text-gray-700">{product.view_count ?? 0} views</p>
                              </div>
                            </div>

                            <div className="flex items-center gap-2 bg-gray-50 border border-gray-100/50 rounded-xl p-2.5">
                              <FiStar size={14} className="text-gray-400" />
                              <div>
                                <p className="text-[8px] font-black text-gray-400 uppercase tracking-wider">Rating</p>
                                <p className="text-[11px] font-extrabold text-gray-700">
                                  {product.average_rating ? `${product.average_rating} (${product.reviews_count ?? 0})` : 'Unrated'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* BUTTON ACTION BAR */}
                      <div className="space-y-3 mt-6 pt-5 border-t border-gray-50">
                        {/* FEATURE ACTION ROW */}
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleFeatureButtonClick(product)}
                            className={`flex-1 h-10 rounded-xl font-bold text-[10px] uppercase tracking-wider transition-all border flex items-center justify-center gap-2 shadow-sm ${
                              product.is_featured 
                                ? "bg-amber-500 text-white border-amber-500 shadow-amber-500/10 hover:bg-amber-600" 
                                : "bg-white text-gray-500 border-gray-200 hover:border-amber-500 hover:text-amber-500 hover:bg-amber-50/10"
                            }`}
                          >
                            <FiStar className={product.is_featured ? "fill-current" : ""} size={13} />
                            {product.is_featured ? "Stop Featuring" : "Feature Product"}
                          </button>
                          
                          <label className="h-10 px-3 bg-white text-gray-500 rounded-xl border border-gray-200 hover:border-[#ff7a00] hover:text-[#ff7a00] hover:bg-orange-50/10 transition-all text-[10px] font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 cursor-pointer shadow-sm" title="Upload Featured Image">
                            <FiUploadCloud size={14} />
                            <span>Image</span>
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                setFeaturedImageByProduct((prev) => ({
                                  ...prev,
                                  [product.id]: file,
                                }));
                                showNotification(`Landscape image selected: ${file.name}`, "info");
                              }}
                            />
                          </label>
                        </div>

                        {featuredImageByProduct[product.id] && (
                          <div className="bg-[#ff7a00]/5 border border-[#ff7a00]/10 rounded-xl p-2.5 flex items-center justify-between">
                            <span className="text-[9px] font-bold text-[#ff7a00] uppercase tracking-wider truncate max-w-[180px]">
                              Ready: {featuredImageByProduct[product.id].name}
                            </span>
                            <button 
                              onClick={() => {
                                setFeaturedImageByProduct((prev) => {
                                  const next = { ...prev };
                                  delete next[product.id];
                                  return next;
                                });
                              }}
                              className="text-[9px] font-bold text-red-500 hover:underline uppercase"
                            >
                              Clear
                            </button>
                          </div>
                        )}

                        {product.featured_image_url && (
                          <div className="bg-amber-50/50 border border-amber-100/50 rounded-xl p-2.5 flex items-center justify-between">
                            <span className="text-[9px] font-bold text-amber-600 uppercase tracking-widest">
                              Landscape Image Uploaded
                            </span>
                            <a 
                              href={product.featured_image_url} 
                              target="_blank" 
                              rel="noreferrer" 
                              className="text-[9px] font-black text-amber-500 hover:underline uppercase tracking-wider"
                            >
                              View
                            </a>
                          </div>
                        )}

                        {/* ADMINISTRATIVE OPERATIONS */}
                        <div className="flex gap-2">
                          {/* AI CHECK BUTTON */}
                          <button
                            onClick={() => handleAICheck(product.id)}
                            className={`flex-1 h-9 rounded-xl font-bold text-[9px] uppercase tracking-wider transition-all border flex items-center justify-center gap-1.5 shadow-sm ${
                              product.ai_checked 
                                ? "bg-indigo-50 text-indigo-600 border-indigo-100 hover:bg-indigo-100" 
                                : "bg-white text-indigo-500 border-indigo-100 hover:bg-indigo-50 hover:text-indigo-600"
                            }`}
                            title="Run AI verification analysis"
                          >
                            <FiCpu size={12} className={!product.ai_checked ? "animate-pulse" : ""} />
                            <span>{product.ai_checked ? "Re-Run AI Check" : "Run AI Check"}</span>
                          </button>

                          <button
                            onClick={() => navigate(`/products/${product.id}/edit?admin=1`)}
                            className="w-9 h-9 bg-gray-50 text-gray-500 rounded-xl hover:bg-gray-800 hover:text-white transition-all border border-gray-200 flex items-center justify-center shadow-sm"
                            title="Edit Product"
                          >
                            <FiEdit size={14} />
                          </button>

                          <button
                            onClick={() => openConfirm(() => handleDeleteProduct(product.id), `DELETE PRODUCT ${product.name.toUpperCase()}`)}
                            className="w-9 h-9 bg-red-50 text-red-400 rounded-xl hover:bg-red-500 hover:text-white transition-all border border-red-150 flex items-center justify-center shadow-sm"
                            title="Delete Product"
                          >
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === "categories" && (
                <div className="space-y-8">
                   <section className="bg-gray-900 rounded-3xl p-8 text-white relative overflow-hidden group">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-[#ff7a00]/10 rounded-full -mr-16 -mt-16 blur-xl group-hover:bg-[#ff7a00]/20 transition-all duration-1000" />
                      <h2 className="text-lg font-bold mb-6 relative z-10">New Market Classification</h2>
                      <form onSubmit={handleCreateCategory} className="grid grid-cols-1 md:grid-cols-3 gap-4 relative z-10">
                        <input
                          type="text"
                          className="w-full h-12 bg-white/5 border border-white/10 rounded-xl px-4 font-bold text-xs uppercase tracking-widest text-white outline-none transition-all placeholder:text-white/20 focus:bg-white/10"
                          placeholder="CATEGORY NAME..."
                          value={newCategoryName}
                          onChange={(e) => setNewCategoryName(e.target.value)}
                          required
                        />
                        <div className="relative">
                          <select
                            className="w-full h-12 bg-white/5 border border-white/10 rounded-xl px-4 font-bold text-[10px] uppercase tracking-widest text-white outline-none appearance-none cursor-pointer focus:bg-white/10"
                            value={newCategoryParentId}
                            onChange={(e) => setNewCategoryParentId(e.target.value)}
                          >
                            <option value="" className="bg-gray-900">MASTER NODE</option>
                            {parentCategories.map((category) => (
                              <option key={category.id} value={category.id} className="bg-gray-900">{localizedCategoryName(category, i18n.language).toUpperCase()}</option>
                            ))}
                          </select>
                          <FiChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none" />
                        </div>
                        <button type="submit" className="w-full h-12 bg-[#ff7a00] text-white font-bold rounded-xl text-[10px] uppercase tracking-widest hover:bg-[#e66c00] transition-all flex items-center justify-center gap-2">
                          <FiPlus size={16} /> Create Node
                        </button>
                      </form>
                    </section>

                    <div className="bg-white border border-gray-100 rounded-3xl p-8 overflow-hidden">
                      <h2 className="text-xl font-bold mb-8">Classification Directory</h2>
                      <div className="overflow-x-auto -mx-8">
                        <table className="w-full text-left">
                          <thead>
                            <tr className="border-b border-gray-50">
                               <th className="px-8 pb-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Classification</th>
                               <th className="px-8 pb-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Type</th>
                               <th className="px-8 pb-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest text-right">Action</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-50">
                            {categories.map((cat) => (
                              <tr key={cat.id} className="group hover:bg-gray-50 transition-all">
                                <td className="px-8 py-6">
                                  <div className="font-bold text-sm text-gray-800 uppercase tracking-wide">{cat.name}</div>
                                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mt-1">{cat.slug}</div>
                                </td>
                                <td className="px-8 py-6">
                                  <span className={`px-3 py-1 border text-[9px] font-bold rounded-lg uppercase tracking-wider ${
                                    !cat.parent_id 
                                      ? "bg-orange-50 border-orange-100 text-[#ff7a00]" 
                                      : "bg-gray-50 border-gray-100 text-gray-500"
                                  }`}>
                                    {!cat.parent_id ? "Master Node" : `Sub-node of ${cat.parent_name || "Unknown"}`}
                                  </span>
                                </td>
                                <td className="px-8 py-6 text-right">
                                  <button
                                    onClick={() => openConfirm(() => handleDeleteCategory(cat.id), `DELETE CATEGORY ${cat.name.toUpperCase()}`)}
                                    className="w-9 h-9 rounded-lg bg-red-50 text-red-400 hover:bg-red-500 hover:text-white transition-all flex items-center justify-center ml-auto border border-red-100"
                                  >
                                    <FiTrash2 size={14} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                </div>
              )}

              {activeTab === "requests" && (
                <div className="space-y-8">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold">Pending Product Requests</h2>
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                      {productRequests.length} Pending
                    </span>
                  </div>

                  {productRequests.length === 0 ? (
                    <div className="bg-white border border-gray-100 rounded-3xl p-10 text-sm font-bold text-gray-400">
                      No pending product requests.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      {productRequests.map((request) => {
                        const current = productReview[request.id] || {};
                        const trustScore = request.trust_score || 0;
                        const scoreColor = trustScore >= 80 ? "text-emerald-500" : trustScore >= 50 ? "text-orange-500" : "text-red-500";
                        const scoreBg = trustScore >= 80 ? "bg-emerald-50" : trustScore >= 50 ? "bg-orange-50" : "bg-red-50";

                        return (
                          <div key={request.id} className="bg-white border border-gray-100 rounded-3xl p-6 space-y-6 hover:shadow-lg transition-all">
                            <div className="flex gap-6">
                              <div className="w-24 h-24 rounded-2xl bg-gray-50 overflow-hidden border border-gray-100 shrink-0">
                                <img src={request.thumbnail_url || "https://placehold.co/200"} alt={request.name} className="w-full h-full object-cover" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <h4 className="font-bold text-gray-800 truncate">{request.name}</h4>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">By {request.supplier?.first_name}</p>
                                  </div>
                                  <div className="text-right">
                                    <div className="text-sm font-bold text-[#ff7a00]">{request.price}</div>
                                    <div className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">{localizedCategoryName(request.category, i18n.language)}</div>
                                  </div>
                                </div>
                                
                                {/* AI TRUST SCORE */}
                                <div className={`mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${scoreBg} border border-current/5`}>
                                   <FiShield size={12} className={scoreColor} />
                                   <span className={`text-[10px] font-black uppercase tracking-wider ${scoreColor}`}>
                                     AI Trust Score: {trustScore}%
                                   </span>
                                </div>
                              </div>
                            </div>

                            {request.ai_checked && request.rejection_reason && (
                              <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">AI Recommendation:</p>
                                <p className="text-xs font-medium text-gray-600 italic">"{request.rejection_reason}"</p>
                              </div>
                            )}

                            <div className="space-y-3">
                              <button
                                onClick={() => handleAICheck(request.id)}
                                className="w-full h-10 bg-gray-900 text-white rounded-xl font-bold text-[10px] uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-black transition-all"
                              >
                                <FiShield size={14} /> Check with AI
                              </button>
                              
                              <div className="grid grid-cols-2 gap-3">
                                <button
                                  onClick={() => setProductReview(prev => ({ ...prev, [request.id]: { status: 'active' } }))}
                                  className={`h-10 rounded-xl font-bold text-[10px] uppercase tracking-wider transition-all border ${
                                    current.status === 'active' 
                                      ? "bg-emerald-500 text-white border-emerald-500" 
                                      : "bg-white text-emerald-600 border-emerald-100 hover:border-emerald-500"
                                  }`}
                                >
                                  Accept
                                </button>
                                <button
                                  onClick={() => setProductReview(prev => ({ ...prev, [request.id]: { status: 'rejected' } }))}
                                  className={`h-10 rounded-xl font-bold text-[10px] uppercase tracking-wider transition-all border ${
                                    current.status === 'rejected' 
                                      ? "bg-red-500 text-white border-red-500" 
                                      : "bg-white text-red-600 border-red-100 hover:border-red-500"
                                  }`}
                                >
                                  Reject
                                </button>
                              </div>

                              {current.status === 'rejected' && (
                                <textarea
                                  placeholder="REJECTION REASON (Sent to user)..."
                                  className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl text-xs font-medium outline-none focus:bg-white focus:border-red-200 transition-all min-h-[80px]"
                                  value={current.reason || request.rejection_reason || ""}
                                  onChange={(e) => setProductReview(prev => ({ ...prev, [request.id]: { ...prev[request.id], reason: e.target.value } }))}
                                />
                              )}

                              {current.status && (
                                <button
                                  onClick={() => handleProductReview(request.id)}
                                  className="w-full h-11 bg-gray-900 text-white font-bold text-[10px] rounded-xl uppercase tracking-[0.2em] hover:bg-black transition-all shadow-lg shadow-gray-200"
                                >
                                  Submit Decision
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {activeTab === "verification" && (
                <div className="space-y-8">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold">Verification Requests</h2>
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                      {verificationRequests.length} Requests
                    </span>
                  </div>

                  {verificationRequests.length === 0 ? (
                    <div className="bg-white border border-gray-100 rounded-3xl p-10 text-sm font-bold text-gray-400">
                      No verification requests yet.
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {verificationRequests.map((request) => {
                        const current = verificationReview[request.user_id] || {};
                        return (
                          <div
                            key={request.user_id}
                            className="bg-white border border-gray-100 rounded-3xl p-6 space-y-5"
                          >
                            <div className="flex flex-col md:flex-row gap-5">
                              <div className="w-20 h-20 rounded-2xl overflow-hidden bg-gray-50 border border-gray-100 shrink-0">
                                <img
                                  src={
                                    request.store_logo_url ||
                                    `https://ui-avatars.com/api/?name=${encodeURIComponent(request.store_name || request.user?.first_name || "S")}&background=ff7a00&color=fff`
                                  }
                                  alt={request.store_name || "Store"}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-1">
                                  <div className="font-bold text-gray-800">
                                    {(request.user?.first_name || "")} {(request.user?.last_name || "")}
                                  </div>
                                  <div className="text-xs font-semibold text-gray-500">
                                    {request.phone_number}
                                  </div>
                                  <div className="text-xs font-semibold text-gray-500">
                                    {request.address}
                                  </div>
                                </div>
                                <div className="flex flex-col md:items-end gap-2">
                                  <span className={`px-3 py-1.5 rounded-full text-[9px] font-bold uppercase tracking-widest ${
                                    request.status === "verified"
                                      ? "bg-emerald-50 text-emerald-600 border border-emerald-100"
                                      : request.status === "rejected"
                                      ? "bg-red-50 text-red-500 border border-red-100"
                                      : "bg-orange-50 text-[#ff7a00] border border-orange-100"
                                  }`}>
                                    {request.status}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="flex gap-6">
                              {request.document_url && (
                                <div className="space-y-2">
                                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">ID Card</p>
                                  <div className="w-40 h-40 rounded-2xl overflow-hidden bg-gray-50 border border-gray-100">
                                    <img
                                      src={request.document_url}
                                      alt="ID Card"
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                  <a href={request.document_url} target="_blank" rel="noreferrer" className="text-[10px] font-bold text-[#ff7a00] hover:underline uppercase tracking-widest">View Full</a>
                                </div>
                              )}
                              {request.selfie_url && (
                                <div className="space-y-2">
                                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Live Selfie</p>
                                  <div className="w-40 h-40 rounded-full overflow-hidden bg-gray-50 border-4 border-[#ff7a00]/10 shadow-inner">
                                    <img
                                      src={request.selfie_url}
                                      alt="Selfie"
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                  <a href={request.selfie_url} target="_blank" rel="noreferrer" className="text-[10px] font-bold text-[#ff7a00] hover:underline uppercase tracking-widest">View Full</a>
                                </div>
                              )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-[220px_1fr_auto] gap-3">
                              <select
                                value={current.status || ""}
                                onChange={(e) =>
                                  setVerificationReview((prev) => ({
                                    ...prev,
                                    [request.user_id]: {
                                      ...prev[request.user_id],
                                      status: e.target.value,
                                    },
                                  }))
                                }
                                className="h-12 bg-gray-50 border border-gray-100 rounded-xl px-4 font-bold text-sm outline-none"
                              >
                                <option value="">Choose action</option>
                                <option value="verified">Verify</option>
                                <option value="rejected">Reject</option>
                              </select>
                              <input
                                value={current.reason || ""}
                                onChange={(e) =>
                                  setVerificationReview((prev) => ({
                                    ...prev,
                                    [request.user_id]: {
                                      ...prev[request.user_id],
                                      reason: e.target.value,
                                    },
                                  }))
                                }
                                placeholder="Reject reason"
                                className="h-12 bg-gray-50 border border-gray-100 rounded-xl px-4 font-semibold text-sm outline-none"
                              />
                              <button
                                onClick={() => handleVerificationReview(request.user_id)}
                                className="h-12 px-6 bg-[#ff7a00] text-white rounded-xl font-bold text-[10px] uppercase tracking-widest"
                              >
                                Update
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {activeTab === "coupons" && (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                  <section className="lg:col-span-4 bg-white border border-gray-100 rounded-3xl p-8 space-y-8 h-fit">
                    <h2 className="text-xl font-bold">New Coupon</h2>
                    <form onSubmit={handleCreateCoupon} className="space-y-6">
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">COUPON CODE</label>
                        <input
                          type="text"
                          className="w-full h-12 bg-gray-50 border border-transparent focus:border-[#ff7a00]/20 rounded-xl px-4 font-bold text-xs uppercase tracking-widest text-[#1d1d1f] outline-none transition-all placeholder:text-gray-300"
                          placeholder="e.g. SAVE20"
                          value={newCouponCode}
                          onChange={(e) => setNewCouponCode(e.target.value)}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">DISCOUNT %</label>
                        <input
                          type="number"
                          className="w-full h-12 bg-gray-50 border border-transparent focus:border-[#ff7a00]/20 rounded-xl px-4 font-bold text-xs uppercase tracking-widest text-[#1d1d1f] outline-none transition-all placeholder:text-gray-300"
                          placeholder="20"
                          value={newDiscount}
                          onChange={(e) => setNewDiscount(e.target.value)}
                          max="100"
                          required
                        />
                      </div>
                      <button type="submit" className="w-full h-12 bg-[#ff7a00] text-white font-bold rounded-xl text-[10px] uppercase tracking-widest hover:bg-[#e66c00] transition-all shadow-lg shadow-orange-500/10">
                        Create Coupon
                      </button>
                    </form>
                  </section>

                  <section className="lg:col-span-8 bg-white border border-gray-100 rounded-3xl p-8 overflow-hidden">
                    <h2 className="text-xl font-bold mb-8">Active Coupons</h2>
                    <div className="overflow-x-auto -mx-8">
                      <table className="w-full text-left">
                        <thead>
                          <tr className="border-b border-gray-50">
                            <th className="px-8 pb-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Code</th>
                            <th className="px-8 pb-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Reduction</th>
                            <th className="px-8 pb-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest text-right">Created</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {coupons.map((coupon) => (
                            <tr key={coupon.id} className="hover:bg-gray-50 transition-all">
                              <td className="px-8 py-6">
                                <span className="px-4 py-1.5 bg-orange-50 text-[#ff7a00] font-bold rounded-lg text-xs uppercase tracking-widest border border-orange-100">{coupon.code}</span>
                              </td>
                              <td className="px-8 py-6 font-bold text-sm text-gray-800">{coupon.discount_percentage}% OFF</td>
                              <td className="px-8 py-6 text-right font-bold text-gray-400 text-[10px] uppercase tracking-widest">
                                {new Date(coupon.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }).toUpperCase()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                </div>
              )}

              {activeTab === "translations" && (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                  <section className="lg:col-span-5 bg-white border border-gray-100 rounded-3xl p-8 space-y-8 h-fit">
                    <div className="space-y-2">
                      <h2 className="text-xl font-bold">Translation Job</h2>
                      <p className="text-sm text-gray-500">
                        Analyze categories and product titles/descriptions, detect the source language automatically, then store both English and French in the database.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={handleStartTranslationJob}
                      disabled={translationStarting || translationJob?.running}
                      className="w-full h-12 rounded-xl bg-[#ff7a00] text-white font-bold text-[10px] uppercase tracking-widest hover:bg-[#e66c00] transition-all shadow-lg shadow-orange-500/10 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      <FiRefreshCw className={translationStarting || translationJob?.running ? "animate-spin" : ""} />
                      {translationJob?.running ? "Job Running" : "Run Translation Job"}
                    </button>

                    <div className="rounded-2xl border border-gray-100 bg-gray-50 p-5 space-y-3">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Automatic Sync</p>
                      <p className="text-sm font-semibold text-gray-700">
                        New product creation already triggers automatic EN/FR translation storage on the backend.
                      </p>
                    </div>
                  </section>

                  <section className="lg:col-span-7 bg-white border border-gray-100 rounded-3xl p-8 space-y-8">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <h2 className="text-xl font-bold">Job Progress</h2>
                        <p className="text-sm text-gray-500">
                          Live status for category and product translation backfill.
                        </p>
                      </div>
                      <span className={`px-4 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest ${
                        translationJob?.running
                          ? "bg-orange-50 text-[#ff7a00] border border-orange-100"
                          : translationJob?.status === "completed"
                          ? "bg-emerald-50 text-emerald-600 border border-emerald-100"
                          : translationJob?.status === "failed"
                          ? "bg-red-50 text-red-500 border border-red-100"
                          : "bg-gray-50 text-gray-500 border border-gray-100"
                      }`}>
                        {translationJob?.status || "idle"}
                      </span>
                    </div>

                    <div className="space-y-3">
                      <div className="h-4 rounded-full bg-gray-100 overflow-hidden">
                        <div
                          className="h-full bg-[#ff7a00] transition-all duration-500"
                          style={{ width: `${Math.max(0, Math.min(100, translationJob?.progress_percent || 0))}%` }}
                        />
                      </div>
                      <div className="flex items-center justify-between text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                        <span>{Math.round(translationJob?.progress_percent || 0)}%</span>
                        <span>{translationJob?.processed || 0} / {translationJob?.total || 0}</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="rounded-2xl border border-gray-100 bg-gray-50 p-5">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Categories</p>
                        <p className="text-2xl font-black text-gray-800">{translationJob?.categories_seen || 0}</p>
                      </div>
                      <div className="rounded-2xl border border-gray-100 bg-gray-50 p-5">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Products</p>
                        <p className="text-2xl font-black text-gray-800">{translationJob?.products_seen || 0}</p>
                      </div>
                      <div className="rounded-2xl border border-gray-100 bg-gray-50 p-5">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Updated</p>
                        <p className="text-2xl font-black text-gray-800">{translationJob?.updated || 0}</p>
                      </div>
                    </div>

                    {translationJob?.last_error && (
                      <div className="rounded-2xl border border-red-100 bg-red-50 p-5">
                        <p className="text-[10px] font-bold text-red-400 uppercase tracking-widest mb-2">Last Error</p>
                        <p className="text-sm font-semibold text-red-600">{translationJob.last_error}</p>
                      </div>
                    )}
                  </section>
                </div>
              )}

              {activeTab === "push" && (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                  <section className="lg:col-span-5 bg-white border border-gray-100 rounded-3xl p-8 space-y-8 h-fit">
                    <div className="space-y-2">
                      <h2 className="text-xl font-bold">Push Notification</h2>
                      <p className="text-sm text-gray-500">
                        Send a custom notification to all registered app devices and in-site notification feeds.
                      </p>
                    </div>

                    <form onSubmit={handleBroadcastNotification} className="space-y-6">
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                          Title
                        </label>
                        <input
                          type="text"
                          className="w-full h-12 bg-gray-50 border border-transparent focus:border-[#ff7a00]/20 rounded-xl px-4 font-bold text-xs uppercase tracking-widest text-[#1d1d1f] outline-none transition-all placeholder:text-gray-300"
                          placeholder="NOTIFICATION TITLE..."
                          value={pushTitle}
                          onChange={(e) => setPushTitle(e.target.value)}
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                          Description
                        </label>
                        <textarea
                          className="w-full min-h-[140px] bg-gray-50 border border-transparent focus:border-[#ff7a00]/20 rounded-xl px-4 py-3 font-bold text-xs text-[#1d1d1f] outline-none transition-all placeholder:text-gray-300 resize-none"
                          placeholder="DESCRIPTION..."
                          value={pushMessage}
                          onChange={(e) => setPushMessage(e.target.value)}
                        />
                      </div>

                      <button
                        type="submit"
                        className="w-full h-12 rounded-xl bg-[#ff7a00] text-white font-bold text-[10px] uppercase tracking-widest hover:bg-[#e66c00] transition-all shadow-lg shadow-orange-500/10"
                      >
                        Push to All Devices
                      </button>
                    </form>
                  </section>

                  <section className="lg:col-span-7 bg-white border border-gray-100 rounded-3xl p-8">
                    <h2 className="text-xl font-bold mb-6">Delivery Scope</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="rounded-2xl border border-gray-100 bg-gray-50 p-5">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Website</p>
                        <p className="text-sm font-semibold text-gray-700">
                          Shows in the website notification panel immediately.
                        </p>
                      </div>
                      <div className="rounded-2xl border border-gray-100 bg-gray-50 p-5">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Mobile App</p>
                        <p className="text-sm font-semibold text-gray-700">
                          Appears in the app notification screen and badge count.
                        </p>
                      </div>
                      <div className="rounded-2xl border border-gray-100 bg-gray-50 p-5">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Push</p>
                        <p className="text-sm font-semibold text-gray-700">
                          Pushes to all registered devices through Firebase.
                        </p>
                      </div>
                    </div>
                  </section>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>

      {/* CONFIRMATION MODAL: Profile style */}
      {showConfirm && (
        <div className="fixed inset-0 z-[5000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-fade-in" onClick={() => setShowConfirm(false)}></div>
          <div className="relative bg-white w-full max-w-md rounded-3xl p-10 shadow-2xl animate-zoom-in text-center">
            <div className="w-20 h-20 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-8 border border-red-100">
              <FiTrash2 size={32} />
            </div>
            
            <h3 className="text-xl font-bold mb-3">Confirm Action</h3>
            <p className="text-sm text-gray-500 font-medium mb-10 leading-relaxed uppercase tracking-tight opacity-70">
              {confirmLabel || "This action cannot be undone. Are you sure you want to proceed?"}
            </p>
            
            <div className="flex gap-4">
              <button 
                onClick={() => setShowConfirm(false)}
                className="flex-1 h-12 bg-gray-50 text-gray-500 font-bold rounded-xl text-xs uppercase tracking-wider hover:bg-gray-100 transition-all"
              >
                Cancel
              </button>
              <button 
                onClick={executeConfirm}
                className="flex-1 h-12 bg-red-500 text-white font-bold rounded-xl text-xs uppercase tracking-wider shadow-lg shadow-red-500/20 hover:bg-red-600 active:scale-95 transition-all"
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}
      {/* FEATURED LANDSCAPE IMAGE UPLOAD MODAL */}
      {showFeaturedImageModal && targetProductForFeatured && (
        <div className="fixed inset-0 z-[5000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-fade-in" onClick={() => {
            setShowFeaturedImageModal(false);
            setTargetProductForFeatured(null);
            setTempFeaturedImage(null);
          }}></div>
          <div className="relative bg-white w-full max-w-lg rounded-3xl p-8 shadow-2xl animate-zoom-in border border-gray-100">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-[#ff7a00]/10 text-[#ff7a00] rounded-2xl flex items-center justify-center mx-auto mb-4 border border-[#ff7a00]/20">
                <FiUploadCloud size={28} />
              </div>
              <h3 className="text-xl font-extrabold text-gray-800">Featured Image Required</h3>
              <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mt-1">To feature: {targetProductForFeatured.name}</p>
            </div>
            
            <div className="bg-gray-50 border border-gray-100 rounded-2xl p-5 mb-6">
              <p className="text-xs text-gray-500 font-medium leading-relaxed">
                Featured products are highlighted in the premium showcase sections of the marketplace home screen. To ensure a gorgeous visual layout, please upload a <strong className="text-gray-800">landscape-oriented image</strong> (recommended 16:9 aspect ratio).
              </p>
            </div>

            {/* DRAG AND DROP / UPLOAD ZONE */}
            <div className="mb-6">
              {tempFeaturedImage ? (
                <div className="relative rounded-2xl overflow-hidden border border-gray-200 aspect-[16/9] bg-gray-50 flex items-center justify-center group">
                  <img 
                    src={URL.createObjectURL(tempFeaturedImage)} 
                    alt="Featured preview" 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button 
                      onClick={() => setTempFeaturedImage(null)}
                      className="bg-red-500 hover:bg-red-600 text-white font-bold text-xs uppercase px-4 py-2 rounded-xl transition-all"
                    >
                      Remove & Choose Other
                    </button>
                  </div>
                </div>
              ) : (
                <label className="border-2 border-dashed border-gray-200 hover:border-[#ff7a00] rounded-2xl p-8 flex flex-col items-center justify-center cursor-pointer bg-gray-50/50 hover:bg-orange-50/10 transition-all duration-300 aspect-[16/9]">
                  <FiUploadCloud className="text-gray-400 group-hover:text-[#ff7a00] mb-3 animate-bounce" size={32} />
                  <span className="text-xs font-bold text-gray-700">Click to upload landscape image</span>
                  <span className="text-[10px] text-gray-400 font-semibold mt-1">Recommended: 1200 x 675 px (PNG, JPG)</span>
                  <input 
                    type="file" 
                    accept="image/*" 
                    className="hidden" 
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) setTempFeaturedImage(file);
                    }}
                  />
                </label>
              )}
            </div>
            
            <div className="flex gap-4">
              <button 
                onClick={() => {
                  setShowFeaturedImageModal(false);
                  setTargetProductForFeatured(null);
                  setTempFeaturedImage(null);
                }}
                className="flex-1 h-12 bg-gray-100 text-gray-600 font-bold rounded-xl text-xs uppercase tracking-wider hover:bg-gray-200 active:scale-95 transition-all"
              >
                Cancel
              </button>
              <button 
                onClick={handleUploadAndFeature}
                disabled={!tempFeaturedImage}
                className={`flex-1 h-12 font-bold rounded-xl text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-2 ${
                  tempFeaturedImage 
                    ? "bg-[#ff7a00] text-white shadow-lg shadow-orange-500/20 hover:bg-[#e66c00] active:scale-95 cursor-pointer" 
                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                }`}
              >
                <FiCheckCircle size={16} />
                Upload & Feature
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
